import { AfterViewChecked, AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, input, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog'
import { Router } from '@angular/router';
import { faWarning } from '@fortawesome/free-solid-svg-icons';
import { faCircleXmark } from '@fortawesome/free-regular-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Subject } from 'rxjs';
import { StateCounty } from 'src/app/models/stateCounty.model';
import { Watershed } from 'src/app/models/watershed.model';
import { ReportService } from 'src/app/services/report.service';
import { DialogService } from 'src/app/services/dialog.service';
import { ConfirmationToastComponent } from 'src/app/components/confirmation-toast/confirmation-toast.component';
import { ReportForest } from 'src/app/models/reportForest.model';

@Component({
  selector: 'app-view-burned-area',
  templateUrl: './view-burned-area.component.html',
  styleUrls: ['./view-burned-area.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})


export class ViewBurnedAreaComponent implements OnInit, AfterViewInit {

  faWarning = faWarning;
  faCircleXmark = faCircleXmark as IconProp;

  @Output() loaded = new EventEmitter();
  html = "<p>Hello</p>"

  report = input.required<any>();
  forests: ReportForest[] = []
  counties: string[] = [];
  rangerDistricts: string[] = [];
  states: string[] = [];
  saveSubject = new Subject<any>();
  simple = input(false);

  streamTypes = [
    "Perennial",
    "Intermittent",
    "Ephemeral",
    "Canal/Ditch/Pipeline",
  ]

  fieldDict: any = {
    "Perennial": "stream_miles_perennial",
    "Intermittent": "stream_miles_intermittent",
    "Ephemeral": "stream_miles_ephemeral",
    "Canal/Ditch/Pipeline": "stream_miles_canal",
    "1 - Basic Custodial Care (Closed)": "roads_miles_basic",
    "2 - High Clearance Vehicles": "roads_miles_clearance",
    "3 - Suitable For Passenger Cars": "roads_miles_passenger",
    "4 - Moderate Degree of User Comfort": "roads_miles_moderate",
    "5 - High Degree of User Comfort": "roads_miles_high",
    "Unknown or Decommissioned": "roads_miles_unknown",
    "Motorized": "transport_miles_motorized",
    "Non-motorized": "transport_miles_non",
    "Snow Trails": "transport_miles_snow",
  }

  maintenanceLevels = [
    "1 - Basic Custodial Care (Closed)",
    "2 - High Clearance Vehicles",
    "3 - Suitable For Passenger Cars",
    "4 - Moderate Degree of User Comfort",
    "5 - High Degree of User Comfort",
    "Unknown or Decommissioned",
  ]

  trailTypes = [
    "Motorized",
    "Non-motorized",
    "Snow Trails",
  ]

  form = this.formBuilder.group({
    baer_name: [""],
    fire_number: [""],
    job_code: ["", Validators.maxLength(2)],
    fire_discovery_date: [null] as unknown as Date[],
    containment_date_est: [null] as unknown as Date[],
    containment_date: [null] as unknown as Date[],

    subwatershed: this.formBuilder.array([]),
    acresBurned: this.formBuilder.array([]),
    streams: this.formBuilder.array([]),
    maintenance: this.formBuilder.array([]),
    trails: this.formBuilder.array([]),

    vegetation_types: [""],
    dominant_soils: [""],
    geologic_types: [""],
    other_roads: [""],
  });
  get subwatershed() {
    return this.form.get("subwatershed") as FormArray;
  }
  get acresBurned() {
    return this.form.get("acresBurned") as FormArray;
  }
  get streams() {
    return this.form.get("streams") as FormArray;
  }
  get maintenance() {
    return this.form.get("maintenance") as FormArray;
  }
  get trails() {
    return this.form.get("trails") as FormArray;
  }

  constructor(
    public dialog: MatDialog,
    private readonly router: Router,
    private readonly formBuilder: FormBuilder,
    public readonly reportService: ReportService,
    private readonly dialogService: DialogService,
    private readonly toast: ConfirmationToastComponent,
  ) {

  }

  ngOnInit(): void {
    this.forests = this.report().forests
    this.counties = this.report().stateCounties.filter((county: StateCounty) => county?.county_name).map((county: StateCounty) => county?.county_name)
    this.states = this.report().stateCounties.filter((state: StateCounty) => state?.state_name != null).map((state: StateCounty) => state?.state_name)
    this.states = [...new Set(this.states)]; //remove duplicates
    this.report().forests.forEach((forest: any) => {
      this.rangerDistricts.push(...forest.ranger_districts.filter((ranger_district: any) => ranger_district?.ranger_district != null).map((ranger_district: any) => ranger_district?.ranger_district));
    })
    this.rangerDistricts = [...new Set(this.rangerDistricts)]; //remove duplicates
    this.buildTableForms()

    this.form.patchValue({
      baer_name: this.report().baer_name,
      fire_number: this.report().IRWIN_unique_fire_identifier,
      job_code: this.report().job_code,
      fire_discovery_date: this.report().fire_discovery_date,
      containment_date_est: this.report().containment_date_est,
      containment_date: this.report().containment_date,

      vegetation_types: this.report().vegetation_types ?? "",
      dominant_soils: this.report().dominant_soils ?? "",
      geologic_types: this.report().geologic_types ?? "",
      other_roads: this.report().other_roads,
    })
  }

  ngAfterViewInit(): void {
    this.loaded.emit(2)
  }

  buildTableForms(): void {
    this.report().watersheds.forEach((watershed: Watershed) => {
      this.subwatershed.push(this.formBuilder.group({
        HU12: watershed.huc12,
        name: watershed.huc12_name,
        totalAcres: watershed.acres_total,
        acresBurned: watershed.acres_burned,
        percentBurned: watershed.percent_burned,
        watershed_id: watershed.watershed_id,
      }))
    })

    for (let forest of this.forests) {
      let questions1: any = {
        forest_id: forest.forest_id
      }
      for (let stream of this.streamTypes) {
        questions1[stream] = [(forest as any)[this.fieldDict[stream]]];
      }
      this.streams.push(this.formBuilder.group(questions1))
      let questions2: any = { "other": 0 }
      for (let level of this.maintenanceLevels) {
        questions2[level] = [(forest as any)[this.fieldDict[level]]];
      }
      this.maintenance.push(this.formBuilder.group(questions2))
      let questions3: any = {}
      for (let trail of this.trailTypes) {
        questions3[trail] = [(forest as any)[this.fieldDict[trail]]];
      }
      this.trails.push(this.formBuilder.group(questions3))
    }
    for (let data of this.report().soil_burn_severities) {
      let existing = this.acresBurned.controls.find((item: any) => {
        return item.value.name == data.ownership
      })
      if (existing) {
        continue
      }

      this.acresBurned.push(this.formBuilder.group({
        name: data.ownership,
        acresBurned: data.acres_burned,
        percentBurned: data.percent_burned,
        report_soil_burn_severity_id: data.report_soil_burn_severity_id,
      }))
    }
  }

  sumRoadMiles(i: number) {
    let values = (this.form.value.maintenance as any)[i]
    values["other"] = 0
    let total = 0
    for (let key in values) {
      total += +(values[key] ?? 0)
    }
    return total
  }

  sumStream(stream: string) {
    let total = 0
    this.form.value.streams?.forEach((item: any) => {
      total += +(item[stream] ?? 0)
    })
    return total
  }

  sumTrail(trail: string) {
    let total = 0
    this.form.value.trails?.forEach((item: any) => {
      total += +(item[trail] ?? 0)
    })
    return total
  }

  sumAcresBurned() {
    let total = 0
    this.form.value.acresBurned?.forEach((item: any) => {
      total += +(item.acresBurned ?? 0)
    })
    return total
  }

  formatDate(date: Date) {
    let year = date.getFullYear();

    let month = (1 + date.getMonth()).toString();
    month = month.length > 1 ? month : '0' + month;

    let day = date.getDate().toString();
    day = day.length > 1 ? day : '0' + day;

    return month + '/' + day + '/' + year;
  }

  customATOB(s: string): string {
    try {
      return atob(s)
    } catch (err) {
      if (err) {
        return s
      }
    }
    return s
  }
}
